<template>
    <div class="field--poland-tax-residence-field">
        <div class="field--poland-tax-residence-field__tax-office-information">
            <div>
                {{ $t(`${TRANSLATION_KEY}.chooseTaxOffice`) }}
            </div>
            <Field
                v-model="formData.polandTaxOfficeName"
                type="text"
                :name="name + 'PolandTaxOfficeName'"
                :placeholder="
                    $t(`${TRANSLATION_KEY}.chooseTaxOfficePlaceholder`)
                "
                :readonly="true"
                :is-numeric="true"
                @focus="toggleTaxOfficesList"
            >
                <span>
                    {{ $t(`${TRANSLATION_KEY}.taxOffice`) }}
                </span>
            </Field>
            <button
                type="button"
                class="field--poland-tax-residence-field__tax-offices-list"
                @click="toggleTaxOfficesList"
            >
                <span>
                    {{ $t(`${TRANSLATION_KEY}.showAllTaxOffices`) }}
                </span>
            </button>
        </div>
        <TaxOfficeListField
            :is-open="isTaxOfficesListOpen"
            :tax-office-data-foreigner="taxOfficeDataForeigner"
            :name="name + 'firstTaxOfficeList'"
            :passed-data="passedData"
            tax-residence="firstTaxResidence"
            @close="toggleTaxOfficesList"
            @office-selected="setTaxOffice"
        />
        <Field
            v-if="isCitizenshipOtherThanPl"
            key="polandTaxResidentPesel"
            v-model="formData.polandTaxResidentPesel"
            type="text"
            :name="name + 'PolandTaxResidentPesel'"
            :placeholder="$t(`${TRANSLATION_KEY}.polandTaxResidentPesel`)"
            cy-data="polandTaxResidentPesel"
            :is-numeric="true"
        >
            <span>
                {{ $t(`${TRANSLATION_KEY}.polandTaxResidentPesel`) }}
            </span>
        </Field>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import Field from '@/components/basic/fields/Field.vue';
import TaxOfficeListField from '@/components/compositeFields/TaxOfficeListField.vue';

import { useStore } from 'vuex';
import { FieldInterface } from '@/types';

export default defineComponent({
    name: 'PolandTaxResidenceField',
    components: {
        Field,
        TaxOfficeListField,
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        taxOfficeDataForeigner: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        isPolandTaxCountry: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input'],
    setup(props, { emit }) {
        const store = useStore();
        const TRANSLATION_KEY = 'compositeFields.PolandTaxResidenceField';

        const isTaxOfficesListOpen = ref(false);
        const isCitizenshipOtherThanPl = ref(false);
        const passedData = ref<FieldInterface>({ key: '', value: '' });

        const formData = ref({
            polandTaxOfficeId: '',
            polandTaxOfficeName: '',
            polandTaxResidentPesel: '',
        });

        const taxResidenceData = computed(() => store.state.formData);

        const toggleTaxOfficesList = () => {
            isTaxOfficesListOpen.value = !isTaxOfficesListOpen.value;
        };

        const closeTaxOfficeList = () => {
            isTaxOfficesListOpen.value = false;
        };

        const setTaxOffice = (data: any) => {
            formData.value.polandTaxOfficeId = data.key;
            formData.value.polandTaxOfficeName = data.value;
            closeTaxOfficeList();
            emitData();
        };

        const emitData = () => {
            emit('input', formData.value);
        };

        const returnService = () => {
            const {
                firstPolandTaxOfficeId,
                firstPolandTaxOfficeName,
                secondPolandTaxOfficeId,
                secondPolandTaxOfficeName,
            } = taxResidenceData.value;

            if (firstPolandTaxOfficeId && firstPolandTaxOfficeName) {
                formData.value.polandTaxOfficeId = firstPolandTaxOfficeId;
                formData.value.polandTaxOfficeName = firstPolandTaxOfficeName;

                passedData.value.key = firstPolandTaxOfficeId;
                passedData.value.value = firstPolandTaxOfficeName;
            } else if (secondPolandTaxOfficeId && secondPolandTaxOfficeName) {
                formData.value.polandTaxOfficeId = secondPolandTaxOfficeId;
                formData.value.polandTaxOfficeName = secondPolandTaxOfficeName;

                passedData.value.key = secondPolandTaxOfficeId;
                passedData.value.value = secondPolandTaxOfficeName;
            }
        };

        const isPeselRequiredForTaxResidence = () => {
            const { citizenship } = taxResidenceData.value;
            if (citizenship !== 'PL') {
                isCitizenshipOtherThanPl.value = true;
            }
        };

        watch(
            [
                () => formData.value.polandTaxOfficeId,
                () => formData.value.polandTaxOfficeName,
                () => formData.value.polandTaxResidentPesel,
            ],
            emitData
        );

        onMounted(() => {
            returnService();
            isPeselRequiredForTaxResidence();
        });

        return {
            TRANSLATION_KEY,
            isTaxOfficesListOpen,
            isCitizenshipOtherThanPl,
            passedData,
            formData,
            taxResidenceData,
            toggleTaxOfficesList,
            closeTaxOfficeList,
            setTaxOffice,
        };
    },
});
</script>

<style scoped>
.field--poland-tax-residence-field__tax-office-information {
    margin-bottom: 10px;
}
</style>
